import { On3IconCheck, On3IconSoftballLogoFull } from '@on3/icons';
import type {
  ErrorData,
  TransferPortalPageProps,
} from '@on3/ui-lib/api/schema/custom-contracts';
import { Link } from '@on3/ui-lib/components/Link';
import { useAuth, useSite } from '@on3/ui-lib/index';
import { externalApi } from '@on3/ui-lib/utils/api';
import { capitalizeFirstLetter } from '@on3/ui-lib/utils/capitalizeFirstLetter';
import { formatMillions } from '@on3/ui-lib/utils/formatters';
import { type AxiosError } from 'axios';
import { EnteredIcon } from 'components/Icons/EnteredIcon';
import { WithdrawnIcon } from 'components/Icons/WithdrawnIcon';
import { PrimaryButton } from 'components/UI/Buttons/PrimaryButton';
import { Text } from 'components/UI/Typography/Text';
import { useRouter } from 'next/router';
import { OrderByType } from 'pages/transfer-portal/[transferType]/[sport]/[[...year]]';
import { type MouseEventHandler, useCallback, useState } from 'react';

import { IndustryComparisonListAlt } from '../Rankings/IndustryComparison/IndustryComparisonListAlt';
import { IndustryComparisonTpList } from '../Rankings/IndustryComparison/IndustryComparisonTpList';
import { Paywall } from '../Rankings/Paywall';
import { TransferPortalFilters } from './TransferPortalFilters';
import { TopHeader } from './TransferPortalHeaders/Top';
import { WireHeader } from './TransferPortalHeaders/Wire';
import { TransferPortalItem } from './TransferPortalItem';
import { TransferPortalNav } from './TransferPortalNav';
import { TransferPortalNavOptions } from './TransferPortalNavOptions';
import styles from './TransferPortalPage.module.scss';

const arr: [] = [];
const payWallKeys = [12];

export const TransferPortalPage = ({
  filters,
  hideFilters,
  playerData,
  pageTitle,
}: TransferPortalPageProps) => {
  const { relatedModel } = playerData || {};

  const { position, type, sport, year } = relatedModel || {};

  const [transferList, setTransferList] = useState(playerData?.list || arr);
  const [pagination, setPagination] = useState(playerData?.pagination);
  const { currentPage, pageCount } = pagination || {};

  const [disable, setDisable] = useState(false);

  const [pageNumber, setPageNumber] = useState(2);
  const page = pageNumber;
  const router = useRouter();
  const { user } = useAuth();
  const { currentSite, sportsData } = useSite();
  const { positions } = filters;
  const {
    college,
    status,
    orderby,
    transferType,
    'industry-comparison': industryComparison,
  } = router.query;
  const isWire = type === 'wire';
  const isTop = type === 'best-available';
  const isCollegeRoute = !!college;

  const noList = playerData?.list?.length === 0;
  const capitalizeStatus = status ? capitalizeFirstLetter(`${status}`) : null;

  const isIndustryRankable = sportsData?.find((s) => s.key === sport?.key)
    ?.isIndustryRankable;

  const showNavOptions =
    !hideFilters && !!positions?.length && isIndustryRankable;
  const hasSubscription = user?.has;
  const showPaywall =
    sport?.key && payWallKeys.includes(sport.key) && !hasSubscription && isTop;
  const payWallList = transferList?.slice(0, 5) || arr;
  const list = showPaywall ? payWallList : transferList || arr;
  const showLoadMoreButton =
    !showPaywall &&
    currentPage &&
    pageCount &&
    transferList?.length > 49 &&
    currentPage < pageCount;
  const listType = isTop ? 'best-available' : 'latest';

  const handleClick: MouseEventHandler = useCallback(async () => {
    setDisable(true);

    const sportKey = sport?.key;
    const params = {
      sportKey,
      year: `${year}`,
      positionAbbr: position?.abbr,
      orderBy: orderby || OrderByType[transferType as keyof typeof OrderByType],
      orgKey: currentSite?.organizationKey,
      status: capitalizeStatus?.includes('Best') ? 'Entered' : capitalizeStatus,
      page,
    };

    try {
      const response = await externalApi.get(
        `/public/rdb/v1/transfers/${listType}/`,
        {
          params,
        },
      );

      setTransferList([...transferList, ...response.data.list]);
      setPagination(response.data.pagination);
      setPageNumber(pageNumber + 1);
    } catch (e: unknown) {
      const { response, message }: AxiosError<ErrorData> =
        e as AxiosError<ErrorData>;

      console.error(
        'DB TranferPortal Error: ',
        response?.data?.message || message,
      );
    } finally {
      setDisable(false);
    }
  }, [
    capitalizeStatus,
    currentSite?.organizationKey,
    listType,
    orderby,
    page,
    pageNumber,
    position?.abbr,
    sport?.key,
    transferList,
    transferType,
    year,
  ]);

  return (
    <section className={styles.block}>
      <header className={styles.mainHeader}>
        <div className={styles.title}>
          <h1>{pageTitle}</h1>
        </div>
        <nav className={styles.nav}>
          <TransferPortalNav type={relatedModel?.type} year={`${year}`} />
          <TransferPortalFilters
            className={styles.filters}
            filters={filters}
            hideFilters={hideFilters}
            relatedModel={relatedModel}
          />
        </nav>
        <div className={styles.additional}>
          {isWire && !isCollegeRoute && (
            <div>
              <ul className={styles.statsList}>
                <li className={styles.statsItem}>
                  <EnteredIcon className={styles.statusIcon} />
                  <Text variant="subtitle1">Entered</Text>
                  <span className={styles.value}>
                    {relatedModel?.playersEnteredCount?.toLocaleString()}
                  </span>
                </li>
                <li className={styles.statsItem}>
                  <On3IconCheck fill="#6f747b" />
                  <Text className={styles.committed} variant="subtitle1">
                    Committed
                  </Text>
                  <span className={styles.value}>
                    {relatedModel?.playersCommittedCount?.toLocaleString()}
                  </span>
                  <span className={styles.percent}>
                    {formatMillions(
                      relatedModel?.playersCommittedPercentage || 0,
                    )}
                    %
                  </span>
                </li>
                <li className={styles.statsItem}>
                  <WithdrawnIcon className={styles.statusIcon} />{' '}
                  <Text className={styles.withdrawn} variant="subtitle1">
                    Withdrawn
                  </Text>
                  <span className={styles.value}>
                    {relatedModel?.playersWithdrawnCount?.toLocaleString()}
                  </span>
                  <span className={styles.percent}>
                    {formatMillions(
                      relatedModel?.playersWithdrawnPercentage || 0,
                    )}
                    %
                  </span>
                </li>
              </ul>
            </div>
          )}
          {showNavOptions && (
            <TransferPortalNavOptions relatedModel={relatedModel} />
          )}
          {sport?.key === 12 && (
            <div className={styles.softballBlock}>
              <div className={styles.softballInner}>
                <span>Softball Rankings Provided by</span>
                <Link href="/softball/">
                  <On3IconSoftballLogoFull height={36} width={80} />
                </Link>
              </div>
            </div>
          )}
        </div>
      </header>
      {!noList && (
        <>
          {!industryComparison && (
            <div>
              {isWire ? <WireHeader /> : <TopHeader />}
              <ol className={styles.transferPortalList}>
                {list.map((player, index) => (
                  <li key={`${player.key}-${index}-${orderby}`}>
                    <TransferPortalItem
                      {...player}
                      index={index}
                      isIndustryRankable={isIndustryRankable}
                      page={currentPage}
                      transferYear={playerData?.relatedModel?.year}
                    />
                  </li>
                ))}
              </ol>
              {showPaywall && (
                <Paywall title={`Showing Top 5 ${sport.name} players.`} />
              )}
              {showLoadMoreButton && (
                <PrimaryButton
                  className={styles.btnLoadMore}
                  disabled={disable}
                  fullWidth={false}
                  onClick={handleClick}
                  variant="text"
                >
                  Load More
                </PrimaryButton>
              )}
            </div>
          )}
          {industryComparison && (
            <div className={styles.industryComparison}>
              <IndustryComparisonTpList
                isTop={isTop}
                pagination={pagination}
                playerData={playerData}
              />
            </div>
          )}
        </>
      )}
      {noList && (
        <Text className={styles.noListText}>
          There are not any players in the transfer portal for this selection.
        </Text>
      )}
    </section>
  );
};
